import React, { FC } from 'react';
import { Box, Grid, Typography, Skeleton } from '@mui/material';
import { isEqual } from 'lodash';
import { SectionList } from 'modules/Document/Section/SectionList';
import { Footnotes } from 'modules/Document/Footnotes/Footnotes';
import { Section } from 'modules/Document/Section/Section';
import { EditableContent } from 'shared/components/form/EditableContent/EditableContent';
import { RenderedDocumentProps, RenderedDocumentSectionsProps } from './types';

const DocumentLoader: FC<{ isLightMode?: boolean; reviewMode?: boolean }> = ({
																																							 isLightMode,
																																							 reviewMode,
																																						 }) => (
  <Box
    sx={{
      bgcolor: isLightMode ? '#fff' : '#222',
      boxShadow: '0px 0px 10px 10px rgba(0,0,0,0.03)',
      p: 4,
      minHeight: '80vh',
      overflowY: reviewMode ? 'scroll' : undefined,
      maxHeight: reviewMode ? 'calc(100vh - 250px)' : undefined,
    }}
    className="print-document-page"
  >
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
      <Skeleton
        variant="text"
        width="60%"
        height={60}
        sx={{
          bgcolor: isLightMode ? 'rgba(0,0,0,0.11)' : 'rgba(255,255,255,0.11)',
          mt: 4,
        }}
      />
    </Box>

    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
      <Skeleton
        variant="text"
        width="20%"
        height={32}
        sx={{
          bgcolor: isLightMode ? 'rgba(0,0,0,0.11)' : 'rgba(255,255,255,0.11)',
        }}
      />
    </Box>

    {[1, 2, 3].map((item) => (
      <Box key={item} sx={{ mb: 4 }}>
        <Skeleton
          variant="text"
          width="40%"
          height={40}
          sx={{
            bgcolor: isLightMode ? 'rgba(0,0,0,0.11)' : 'rgba(255,255,255,0.11)',
            mb: 2,
          }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={120}
          sx={{
            bgcolor: isLightMode ? 'rgba(0,0,0,0.11)' : 'rgba(255,255,255,0.11)',
            borderRadius: 1,
            mb: 2,
          }}
        />
        <Skeleton
          variant="text"
          width="80%"
          height={20}
          sx={{
            bgcolor: isLightMode ? 'rgba(0,0,0,0.11)' : 'rgba(255,255,255,0.11)',
          }}
        />
        <Skeleton
          variant="text"
          width="70%"
          height={20}
          sx={{
            bgcolor: isLightMode ? 'rgba(0,0,0,0.11)' : 'rgba(255,255,255,0.11)',
          }}
        />
      </Box>
    ))}
  </Box>
);

const DocumentError: FC<{
	error: string;
	isLightMode?: boolean;
	reviewMode?: boolean;
	onRetry?: () => void;
}> = ({ error, isLightMode, reviewMode, onRetry }) => (
  <Box
    sx={{
      bgcolor: isLightMode ? '#fff' : '#222',
      boxShadow: '0px 0px 10px 10px rgba(0,0,0,0.03)',
      p: 4,
      minHeight: '80vh',
      overflowY: reviewMode ? 'scroll' : undefined,
      maxHeight: reviewMode ? 'calc(100vh - 250px)' : undefined,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    className="print-document-page"
  >
    <Typography
      variant="h6"
      color="error"
      align="center"
      sx={{ mb: 2 }}
    >
				Error loading document
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ mb: 2 }}
    >
      {error}
    </Typography>
    {onRetry && (
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          px: 3,
          py: 1,
          bgcolor: 'primary.main',
          color: 'white',
          border: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
        }}
      >
						Try again
      </Box>
    )}
  </Box>
);

export const RenderDocumentContainer: FC<any> = (
  {
    reviewMode,
    isPrintMode,
    showDocumentSidebar,
    showRightContainer,
    children,
    id,
  },
) => {
  return (
    <Grid
      id={id}
      item
      sm={reviewMode ? 6 : undefined}
      sx={theme => {
        if (isPrintMode) {
          return {
            p: 0,
            width: '100%',
          };
        } else {
          return {
            py: '8px !important',
            px: '16px !important',
            maxWidth: '1200px !important',
            display: reviewMode ? 'grid' : undefined,
            width: '100%',
            [theme.breakpoints.between('md', 'xl')]: {
              maxWidth: `calc(min(100vw, 1200px) ${showDocumentSidebar ? '- 330px' : ''}  ${showRightContainer ? '- 370px' : ''}) !important`,
            },
          };
        }
      }}
    >
      {children}
    </Grid>
  );
};

const RenderedDocumentSections = React.memo<RenderedDocumentSectionsProps>(({
  sections,
  sectionsRaw,
  showComments,
}) => (
  <>
    {sections.map((section: any) => (
      <Section
        key={section.slug}
        section={section}
        siblings={sectionsRaw}
        showComments={showComments}
      />
    ))}
  </>
), (oldProps, newProps) => {
  return (
    isEqual(oldProps.sections, newProps.sections)
			&& isEqual(oldProps.sectionsRaw, newProps.sectionsRaw)
  );
});

interface RenderedDocumentPropsWithLoading extends RenderedDocumentProps {
	isLoading?: boolean;
	error?: string | null;
	onRetry?: () => void;
}

export const RenderedDocument = React.memo<RenderedDocumentPropsWithLoading>((
  {
    document,
    sections,
    reviewMode,
    reviewModeTitle,
    showSectionList,
    isLightMode,
    isPrintPreview,
    isCanEditTitle,
    showComments,
    isLoading = false,
    error = null,
    onRetry,
  },
) => {
  if (isLoading) {
    return (
      <>
        {reviewMode && reviewModeTitle && (
          <Typography
            variant="h6"
            align="center"
            color={isLightMode ? 'rgba(0,0,0,0.5)' : 'rgba(255, 255, 255, 0.5)'}
            sx={{ pb: 1 }}
          >
            {reviewModeTitle}
          </Typography>
        )}
        <DocumentLoader isLightMode={isLightMode} reviewMode={reviewMode}/>
      </>
    );
  }

  if (error) {
    return (
      <>
        {reviewMode && reviewModeTitle && (
          <Typography
            variant="h6"
            align="center"
            color={isLightMode ? 'rgba(0,0,0,0.5)' : 'rgba(255, 255, 255, 0.5)'}
            sx={{ pb: 1 }}
          >
            {reviewModeTitle}
          </Typography>
        )}
        <DocumentError
          error={error}
          isLightMode={isLightMode}
          reviewMode={reviewMode}
          onRetry={onRetry}
        />
      </>
    );
  }

  if (!document) {
    return (
      <>
        {reviewMode && reviewModeTitle && (
          <Typography
            variant="h6"
            align="center"
            color={isLightMode ? 'rgba(0,0,0,0.5)' : 'rgba(255, 255, 255, 0.5)'}
            sx={{ pb: 1 }}
          >
            {reviewModeTitle}
          </Typography>
        )}
        <Box
          sx={{
            bgcolor: isLightMode ? '#fff' : '#222',
            boxShadow: '0px 0px 10px 10px rgba(0,0,0,0.03)',
            p: 4,
            minHeight: '80vh',
            overflowY: reviewMode ? 'scroll' : undefined,
            maxHeight: reviewMode ? 'calc(100vh - 250px)' : undefined,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="print-document-page"
        >
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
          >
							Awaiting document data...
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {reviewMode && reviewModeTitle && (
        <Typography
          variant="h6"
          align="center"
          color={isLightMode ? 'rgba(0,0,0,0.5)' : 'rgba(255, 255, 255, 0.5)'} sx={{ pb: 1 }}
        >
          {reviewModeTitle}
        </Typography>
      )}
      <Box
        sx={{
          bgcolor: isLightMode ? '#fff' : '#222',
          boxShadow: '0px 0px 10px 10px rgba(0,0,0,0.03)',
          p: 4,
          minHeight: '80vh',
          overflowY: reviewMode ? 'scroll' : undefined,
          maxHeight: reviewMode ? 'calc(100vh - 250px)' : undefined,
        }}
        className="print-document-page"
      >
        <Typography
          variant="h3"
          color="primary"
          align="center"
          sx={{ wordBreak: 'break-word', mt: 4 }}
          id="documentTitle"
        >
          <EditableContent
            cla={{ ...document, id: document.id }}
            disableEnterKey
            canEdit={isCanEditTitle}
          >
            {document?.stripped_title}
          </EditableContent>
        </Typography>
        {/*{document?.published_at && (*/}
        {/*  <Typography*/}
        {/*    align="center"*/}
        {/*    color="secondary"*/}
        {/*    variant="h5"*/}
        {/*    sx={{ mt: 2 }}*/}
        {/*    id="documentPublishedAt"*/}
        {/*  >*/}
        {/*    {format(new Date(document.published_at), 'dd/MM/yyyy')}*/}
        {/*  </Typography>*/}
        {/*)}*/}
        {(!reviewMode && showSectionList) && <SectionList document={document}/>}
        <RenderedDocumentSections
          sections={sections}
          sectionsRaw={document?.section_set}
          showComments={showComments}
        />
        {isPrintPreview && (
          <Footnotes/>
        )}
      </Box>
    </>
  );
}, (oldProps, newProps) => {
  return (
    isEqual(oldProps.document, newProps.document)
			&& isEqual(oldProps.sections, newProps.sections)
			&& oldProps.reviewMode === newProps.reviewMode
			&& oldProps.reviewModeTitle === newProps.reviewModeTitle
			&& oldProps.showSectionList === newProps.showSectionList
			&& oldProps.isLightMode === newProps.isLightMode
			&& oldProps.isPrintPreview === newProps.isPrintPreview
			&& oldProps.showComments === newProps.showComments
			&& oldProps.isCanEditTitle === newProps.isCanEditTitle
			&& oldProps.isLoading === newProps.isLoading
			&& oldProps.error === newProps.error
  );
});
